import React, { useRef } from 'react';
import { Button } from '@mui/material';
import PopoverComponent, { PopoverComponentRef } from 'src/components/Popover';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import useYupValidationResolver from 'src/hooks/useYupValidationResolver';
import toast from 'react-hot-toast';
import { confirmWrapper } from 'src/components/Dialog';
import { SeperatorIcon } from 'src/icons';
import RHFInput from 'src/components/elements/Input/RHFInput';
import { Confirmation } from 'src/components';
import * as yup from 'yup';
import { DialogContentProps } from 'src/components/Dialog/Dialog';
import { SkillFormSubmitHandler, SkillFormValues } from '../../types';

interface AddSkillProps extends DialogContentProps {
  defaultValues?: SkillFormValues;
  disabled: boolean;
  readOnly?: boolean;
  onCreateSkill: (data: SkillFormSubmitHandler) => void;
}

const validationSchema = yup.object().shape({
  value: yup.string().typeError('Enter Skill').required('Skill required'),
});

const AddSkill = function AddActivity(props: AddSkillProps) {
  const {
    defaultValues,
    hideDialogLoading,
    showDialogLoading,
    close,
    onCreateSkill,
  } = props;

  const approveRef = useRef<PopoverComponentRef>(null);
  const resolver = useYupValidationResolver(validationSchema);
  const methods = useForm<SkillFormValues>({
    defaultValues,
    resolver,
  });

  const {
    handleSubmit, reset,
    formState: { isDirty },
  } = methods;
  const { id } = defaultValues || {};
  const showLoading = (message: string) => {
    if (showDialogLoading) {
      showDialogLoading(message);
    }
  };

  const hideLoading = () => {
    if (hideDialogLoading) {
      hideDialogLoading();
    }
  };

  const submitHandler: SubmitHandler<SkillFormValues> = async () => {
    if (Object.keys(methods.formState.dirtyFields).length > 0) {
      if (approveRef.current?.open) {
        approveRef.current?.open();
      }
    } else {
      toast('No changes', {
        position: 'bottom-center',
      });
    }
  };

  const handleCreate = async () => {
    const data = methods.getValues();
    try {
      if (Object.keys(methods.formState.dirtyFields).length > 0) {
        const loadingMessage = id ? 'Updating Skill...' : 'Creating New Skill ...';
        showLoading(loadingMessage);
        const userFormObj: SkillFormSubmitHandler = {
          values: data,
          formInstance: methods,
        };
        await onCreateSkill(userFormObj);
        if (close) {
          close();
          const message = id ? 'Skill updated successfully!' : 'Skill created successfully!';
          await confirmWrapper(message, {
            hideSecondaryBtn: true,
            hidePrimaryBtn: true,
            variant: 'success',
            autoClose: true,
          });
        }
      } else {
        toast('No changes', {
          position: 'bottom-center',
        });
      }
    } catch (e) {
      console.info('...');
      // close confirmation popover
      if (approveRef.current?.close) approveRef.current?.close();
    } finally {
      hideLoading();
    }
  };

  const confirmationMessage = id ? 'Are you sure you want to update the skill?' : 'Are you sure you want to add new skill?';

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(submitHandler)}>
        <div className="flex justify-center items-center w-full h-[60px] py-10">
          <SeperatorIcon stopColor="#D9D9D9" className="rotate-90" width={90} />
          <div className="flex justify-center text-[#28364C] font-medium text-base pl-[13px]">
            {id ? 'Edit Skill' : 'Add New Skill'}
          </div>
          <SeperatorIcon stopColor="#D9D9D9" className="rotate-90" width={90} />
        </div>

        <div className="flex items-start justify-center px-[10%] gap-7 bg-[#FAFAFA] py-[5%]">
          <div className="w-[180px]">
            <RHFInput name="value" control={methods.control} label="Skill Name" />
          </div>
        </div>

        <div className="flex justify-center items-center py-[20px] gap-4">
          <Button
            className="w-[114px]"
            variant="outlined"
            disabled={!isDirty}
            onClick={() => reset({})}
            data-testid="add-activity/reset"
          >
            Reset
          </Button>
          <PopoverComponent
            ref={approveRef}
            controlled
            Component={(
              <Confirmation
                variant="warning"
                text={confirmationMessage}
                confirm={handleCreate}
              />
            )}
          >
            <Button
              className="w-[114px]"
              type="submit"
              color="primary"
              variant="contained"
              data-testid="add-activity/add-or-update"
            >
              {id ? 'Update' : 'Add'}
            </Button>
          </PopoverComponent>
        </div>
      </form>
    </FormProvider>
  );
};

AddSkill.defaultProps = {
  readOnly: false,
  defaultValues: undefined,
};

AddSkill.propTypes = {

};

export default AddSkill;
